import React, { useState, useEffect } from 'react';
import { AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { replacePlaceholders } from '../utils/configHelpers';
import { getMerchantHeaderClasses, getMerchantButtonClasses } from '../utils/merchantColors';
import { t, pickConfigText, resolveConfigText } from '../utils/i18n';

/**
 * Dynamic Question Set Component
 *
 * Handles rendering of dynamic questions from configuration
 * Supports three action types:
 * - proceed: User can continue
 * - block: Shows warning, disables next button
 * - require_details: Shows text input, requires completion
 */
const DynamicQuestionSet = ({
  config,
  language = 'de',
  merchant = null,
  itemData = null, // For item-level: { bookingItem, inventoryItem }
  onComplete,
  onBack = null, // For item-level: callback to navigate to previous item
  isLastItem = true, // For item-level: whether this is the last item (affects button label)
  isLastSection = true, // Whether this is the final section before submission (affects button label)
  photosFollow = null // For item-level: (answers) => bool, whether a photo step will follow these questions
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState(null);

  const questions = config?.questions || [];
  // Header: exact language or the built-in generic header. Instructions and
  // question texts fall back across languages (requested → en → any) since
  // there is no built-in text to substitute.
  const headerText = pickConfigText(config?.header_text, language) || t(language, 'questionsHeader');
  const instructions = resolveConfigText(config?.instructions, language);
  const showInstructions = config?.show_instructions !== false;

  // Handle empty questions - must call hooks before any conditional returns
  useEffect(() => {
    if (questions.length === 0) {
      // Use timeout to avoid calling during render
      const timer = setTimeout(() => {
        onComplete?.({});
      }, 0);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions.length]);

  // Early return after all hooks
  if (questions.length === 0) {
    return null;
  }

  const currentQuestion = questions[currentQuestionIndex];
  const isFirstQuestion = currentQuestionIndex === 0;
  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  // Replace placeholders in text
  const getDisplayText = (text) => {
    if (!text) return '';
    if (!itemData) return text;

    return replacePlaceholders(text, itemData.bookingItem, itemData.inventoryItem);
  };

  // Check if user can proceed from current question
  const canProceedFromCurrentQuestion = () => {
    const answer = answers[currentQuestion.id];

    // Must have an answer if question is required
    if (currentQuestion.required && !answer) {
      return false;
    }

    // If no answer selected yet
    if (!answer) {
      return !currentQuestion.required;
    }

    // Find selected option
    const selectedOption = currentQuestion.options?.find(opt => opt.value === answer);
    if (!selectedOption) {
      return !currentQuestion.required;
    }

    // Check if blocked
    if (selectedOption.action === 'block') {
      return false;
    }

    // Check if requires details and details are provided
    if (selectedOption.action === 'require_details') {
      const detailsFieldName = selectedOption.details_config?.field_name;
      if (!detailsFieldName) return false;

      const detailsValue = answers[detailsFieldName];
      if (selectedOption.details_config?.required && !detailsValue?.trim()) {
        return false;
      }
    }

    return true;
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    setError(null);
  };

  const handleDetailsChange = (fieldName, value) => {
    setAnswers(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleBack = () => {
    if (isFirstQuestion) {
      // If on first question and onBack provided, navigate to previous item
      if (onBack) {
        onBack();
      }
      return;
    }
    setCurrentQuestionIndex(prev => prev - 1);
    setError(null);
  };

  const handleNext = () => {
    console.log('[DynamicQuestionSet] handleNext called, currentQuestion:', currentQuestionIndex, 'isLast:', isLastQuestion, 'answers:', answers);

    if (!canProceedFromCurrentQuestion()) {
      console.log('[DynamicQuestionSet] Cannot proceed from current question');
      setError(t(language, 'answerBeforeProceeding'));
      return;
    }

    if (isLastQuestion) {
      // All questions answered, complete
      console.log('[DynamicQuestionSet] Last question complete, calling onComplete with answers:', answers);
      onComplete?.(answers);
    } else {
      console.log('[DynamicQuestionSet] Moving to next question');
      setCurrentQuestionIndex(prev => prev + 1);
      setError(null);
    }
  };

  // Get currently selected option
  const currentAnswer = answers[currentQuestion.id];
  const selectedOption = currentQuestion.options?.find(opt => opt.value === currentAnswer);
  const isBlocked = selectedOption?.action === 'block';
  const requiresDetails = selectedOption?.action === 'require_details';

  // For item-level questions: whether a photo step will follow (depends on the
  // current answers, e.g. required_if_issues). When it does, this question set
  // is not the final step, so the button should read "Weiter" not "Abschließen".
  const photoStepFollows = typeof photosFollow === 'function' ? photosFollow(answers) : false;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className={`${getMerchantHeaderClasses(merchant)} p-6`}>
          <h1 className="text-2xl font-bold">{getDisplayText(headerText)}</h1>
          {showInstructions && instructions && (
            <p className="mt-2 opacity-90">{getDisplayText(instructions)}</p>
          )}
          {/* Progress — only meaningful with more than one question */}
          {questions.length > 1 && (
            <>
              <div className="mt-4 flex items-center justify-between text-sm opacity-90">
                <span>
                  {t(language, 'questionProgress', currentQuestionIndex + 1, questions.length)}
                </span>
                <span>{Math.round(((currentQuestionIndex + 1) / questions.length) * 100)}%</span>
              </div>
              {/* Progress Bar */}
              <div className="mt-2 h-2 bg-white bg-opacity-30 rounded-full overflow-hidden">
                <div
                  className="h-full bg-white transition-all duration-300"
                  style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                />
              </div>
            </>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
        {/* Preamble (if exists) */}
        {resolveConfigText(currentQuestion.preamble, language) && (
          <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
            <p className="text-gray-700">
              {getDisplayText(resolveConfigText(currentQuestion.preamble, language))}
            </p>
          </div>
        )}

        {/* Question Text */}
        <h2 className="text-xl font-bold text-gray-800 mb-6">
          {getDisplayText(resolveConfigText(currentQuestion.text, language))}
        </h2>

        {/* Options */}
        <div className="space-y-4 mb-6">
          {currentQuestion.options.map((option) => {
            const isSelected = currentAnswer === option.value;

            return (
              <div key={option.value}>
                <label
                  className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    isSelected
                      ? 'merchant-border merchant-bg-light'
                      : 'border-gray-300 hover:merchant-border-light hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name={currentQuestion.id}
                    value={option.value}
                    checked={isSelected}
                    onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                    className="w-5 h-5 merchant-text focus:ring-2 focus:ring-offset-0"
                    style={{ accentColor: merchant?.color1 || '#f43f5e' }}
                  />
                  <span className="ml-3 text-gray-800 font-medium">
                    {getDisplayText(resolveConfigText(option.label, language))}
                  </span>
                </label>

                {/* Blocking Message */}
                {isSelected && option.action === 'block' && (
                  <div className="mt-3 p-4 bg-orange-100 border-l-4 border-orange-500 rounded">
                    <div className="flex items-start">
                      <AlertTriangle className="w-6 h-6 text-orange-500 mr-3 flex-shrink-0 mt-0.5" />
                      <p className="text-orange-800">
                        {getDisplayText(resolveConfigText(option.blocking_message, language))}
                      </p>
                    </div>
                  </div>
                )}

                {/* Details Input */}
                {isSelected && option.action === 'require_details' && (
                  <div className="mt-3">
                    <textarea
                      className="w-full p-3 border-2 border-gray-300 rounded-lg focus:merchant-border focus:ring-2 focus:ring-offset-0 transition-colors"
                      style={{ '--tw-ring-color': merchant?.color1 ? `${merchant.color1}33` : '#f43f5e33' }}
                      rows="3"
                      placeholder={getDisplayText(resolveConfigText(option.details_config?.placeholder, language))}
                      value={answers[option.details_config?.field_name] || ''}
                      onChange={(e) => handleDetailsChange(option.details_config?.field_name, e.target.value)}
                      required={option.details_config?.required}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-200 p-6 bg-gray-50">
          <div className="flex gap-4">
            <button
              onClick={handleBack}
              disabled={isFirstQuestion && !onBack}
              className={`flex items-center px-6 py-3 rounded-lg font-semibold transition-colors ${
                isFirstQuestion && !onBack
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              {t(language, 'back')}
            </button>

            <button
              onClick={handleNext}
              disabled={!canProceedFromCurrentQuestion()}
              className={`flex-1 flex items-center justify-center px-6 py-3 rounded-lg font-semibold transition-colors ${
                canProceedFromCurrentQuestion()
                  ? getMerchantButtonClasses(merchant)
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isLastQuestion && isLastItem && isLastSection && !photoStepFollows
                ? t(language, 'complete')
                : t(language, 'next')
              }
              {!(isLastQuestion && isLastItem && isLastSection && !photoStepFollows) && <ChevronRight className="w-5 h-5 ml-1" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DynamicQuestionSet;
